import { Fragment } from 'react'
import { motion } from 'framer-motion'
import Markdown from 'react-markdown'

import ChatBubbleStem from './chat-bubble-stem'
import SimpleTooltip from '../ui/simple-tooltip'
import { cx } from '@/utils/strings'
import AngleLeftIcon from '@/icons/bwi/angle-left.svg'
import SourceBadge from './source-badge'
import LoadingDots from './loading-dots'
import FeedbackButtons from './feedback-buttons'
import { OnyxDocument } from './types'
import 'property-information'

type ChatBubbleProps = {
  message: string
  from: 'user' | 'ai'
  state: 'streaming' | 'complete' | 'error'
  isSelected?: boolean
  animated?: boolean
  documents?: OnyxDocument[]
  chatMessageId?: number
  onViewSources?: () => void
  onFeedback?: (feedback: 'positive' | 'negative') => void
}

export default function ChatBubble({
  message,
  from,
  state,
  documents,
  animated = true,
  isSelected = false,
  onViewSources,
  onFeedback,
}: ChatBubbleProps) {
  return (
    <motion.div
      className={cx('relative inline-block', from === 'user' && 'pl-4 md:pl-8', from === 'ai' && 'pr-4 md:pr-8')}
      transition={{
        type: 'spring',
        damping: 10,
        stiffness: 100,
        staggerChildren: 0.5,
        staggerDirection: -1,
      }}
      initial={
        animated && {
          y: -15,
        }
      }
      animate={
        animated && {
          y: 0,
        }
      }
    >
      <motion.div
        className={cx(
          'absolute bottom-0 w-[18px] h-[23px] transition-colors',
          from === 'user' && 'text-primaryBlue -right-2',
          from === 'ai' && '-left-2',
          !isSelected && from === 'ai' && (state === 'complete' || state === 'streaming') && 'text-secondary-100',
          !isSelected && from === 'ai' && state === 'error' && 'text-red-700',
          isSelected && 'text-blue-100 dark:text-deepBlue'
        )}
        initial={{ rotate: 0 }}
        animate={{ rotate: 0 }}
        transition={{ type: 'spring', damping: 10, stiffness: 100 }}
        style={
          from === 'ai'
            ? {
                transform: 'scaleX(-1)',
              }
            : {}
        }
      >
        <ChatBubbleStem reverse={from === 'ai'} />
      </motion.div>
      <motion.div
        className={cx(
          'rounded-[28px] inline-block max-w-full px-8 py-4 transition-colors',
          from === 'user' && 'bg-primaryBlue text-white origin-bottom-right',
          !isSelected &&
            from === 'ai' &&
            (state === 'complete' || state === 'streaming') &&
            'bg-secondary-100 text-neutral-800 dark:text-white py-5 origin-bottom-left',
          !isSelected && from === 'ai' && state === 'error' && 'bg-red-700 text-white origin-bottom-left',
          isSelected && 'bg-blue-100 text-neutral-800 dark:bg-deepBlue dark:text-white'
        )}
        initial={animated && { scale: 0.6 }}
        animate={animated && { scale: 1 }}
        transition={{ ease: 'easeOut', duration: 0.2 }}
      >
        <Markdown
          components={{
            p: ({ node, ...props }) => <p {...props} className="m-0" />,
            ol: ({ node, ...props }) => <ol {...props} className="pl-8" />,
            ul: ({ node, ...props }) => <ul {...props} className="pl-8" />,
            a: ({ node, children, ...props }) => {
              const citationNumber = children?.toString().match(/\[(\d+)\]/)
              const document = citationNumber?.[1] && documents?.[Number(citationNumber[1]) - 1]
              const Wrapper = document ? SimpleTooltip : Fragment
              return (
                <Wrapper
                  content={
                    document && (
                      <div className="max-w-[300px] overflow-hidden text-ellipsis">
                        {document && <SourceBadge document={document} />}
                        <a
                          href={document.link}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-base text-primaryBlue dark:text-white whitespace-pre no-underline"
                        >
                          {document.semantic_identifier}
                        </a>
                      </div>
                    )
                  }
                >
                  <a
                    {...props}
                    className="bg-primaryBlue text-white rounded-full no-underline text-sm inline-flex items-center justify-center px-[6px]"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {children?.toString().replace(/[\[\]]/g, '')}
                  </a>
                </Wrapper>
              )
            },
          }}
        >
          {message}
        </Markdown>

        {state === 'complete' && from === 'ai' && (
          <div className="flex items-center justify-between mt-4">
            {documents && documents.length > 0 && (
              <button className="text-primaryBlue text-base flex items-center gap-2" onClick={onViewSources}>
                <AngleLeftIcon className="w-[10px] [&_path]:stroke-[2px] [&_path]:stroke-primaryBlue" />
                <span>View top sources</span>
              </button>
            )}
            <div className="self-end">
              <FeedbackButtons onFeedback={(feedback) => onFeedback?.(feedback)} />
            </div>
          </div>
        )}
        {state === 'streaming' && (
          <div className="w-6 mt-2">
            <LoadingDots size="sm" color="Light Gray" />
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}
